import { useClientTheme } from "@/app/components/ThemeContext"

/**
 * Shared shell for the settings pages: a bordered card with a header row (icon
 * tile + title + subtitle, and an optional right-aligned action) above the body.
 * Keeps Profile and Company Settings aligned to the same spacing so switching
 * tabs feels like one surface. Callers own the body — pass padded content for a
 * form, or a full-bleed table.
 */
export function SettingsCard({
  icon,
  title,
  subtitle,
  action,
  children,
}: {
  icon: React.ReactNode
  title: string
  subtitle?: string
  action?: React.ReactNode
  children: React.ReactNode
}) {
  const { theme } = useClientTheme()

  return (
    <div
      className="rounded-lg overflow-hidden"
      style={{ backgroundColor: theme.bgSurface, border: `1px solid ${theme.borderColor}` }}
    >
      <div
        className="flex items-center justify-between gap-4 px-6 py-5"
        style={{ borderBottom: `1px solid ${theme.borderColor}` }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="flex items-center justify-center flex-shrink-0 rounded-lg"
            style={{
              width: "34px",
              height: "34px",
              backgroundColor: theme.sidebarActiveBg,
              color: theme.brandAccent,
            }}
          >
            {icon}
          </div>
          <div className="min-w-0">
            <h1
              style={{
                fontFamily: "var(--font-heading)",
                fontSize: "16px",
                fontWeight: 600,
                color: theme.textPrimary,
                lineHeight: 1.25,
              }}
            >
              {title}
            </h1>
            {subtitle && (
              <p
                style={{
                  fontFamily: "var(--font-ui)",
                  fontSize: "13px",
                  color: theme.textMuted,
                  lineHeight: 1.3,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {subtitle}
              </p>
            )}
          </div>
        </div>
        {action && <div className="flex-shrink-0">{action}</div>}
      </div>
      {children}
    </div>
  )
}
